<template>
  <div>
    <!-- Company Logo -->
    <company-logo />
    <!-- Sign Up Form -->
    <Form
      @submit="handleSignUp"
      :validation-schema="schema"
      v-slot="{ values, errors }"
    >
      <!-- Name -->
      <div class="mt-3 ms-4 me-4">
        <Field
          name="name"
          :value="name"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="text"
            placeholder="Name"
            v-model:value="name"
            @update:value="handleChange"
            :errorValidate="errorMessage"
          />
        </Field>
      </div>
      <!-- Phone number -->
      <div class="mt-3 ms-4 me-4">
        <Field
          name="phone"
          :value="enteredPhoneNumber"
          v-slot="{ handleChange, errorMessage }"
        >
          <phone-input-field
            v-model:phoneNumber="enteredPhoneNumber"
            @update:phoneNumber="handleChange"
            :errorValidate="errorMessage"
            @country-code="updateCountryCode"
          />
        </Field>
      </div>
      <!-- Password -->
      <div class="mt-3 ms-4 me-4">
        <Field
          name="password"
          :value="password"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="password"
            placeholder="Password"
            v-model:value.trim="password"
            @update:value="handleChange"
            :errorValidate="errorMessage"
          />
        </Field>
      </div>
      <!-- Confirm Password -->
      <div class="mt-3 ms-4 me-4">
        <Field
          name="confirmPassword"
          :value="confirmPassword"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="password"
            placeholder="Confirm Password"
            v-model:value.trim="confirmPassword"
            @update:value="handleChange"
            :errorValidate="errorMessage"
          />
        </Field>
      </div>
      <!-- Email  -->
      <div class="mt-3 ms-4 me-4">
        <Field
          name="email"
          :value="email"
          v-slot="{ handleChange, errorMessage }"
        >
          <default-input-field
            type="text"
            placeholder="Email"
            v-model:value.trim="email"
            @update:value="handleChange"
            :errorValidate="errorMessage"
          />
        </Field>
      </div>
      <!-- Select Branch Dropdownlist modal -->
      <div class="mt-3 ms-4 me-4">
        <div data-bs-toggle="modal" data-bs-target="#branch">
          <div
            class="me-2 py-2 px-2 rounded form-control style-branch-container"
          >
            <div class="d-flex flex-row style-branch-content">
              <span class="ms-1">{{ selectedBranchName }}</span>
              <i class="bi bi-caret-down-fill ms-auto scss-medium-font"></i>
            </div>
          </div>
        </div>
        <!-- Modal -->
        <div
          class="modal fade"
          id="branch"
          tabindex="-1"
          aria-labelledby="branchModalLabel"
        >
          <div class="modal-dialog modal-dialog-centered modal-sm">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="branchModalLabel">
                  Outlet Selection
                </h5>
              </div>
              <div class="modal-body style-scroll pt-1">
                <div
                  v-for="branch in branches"
                  :key="branch.id"
                  class="d-flex text-start align-items-center style-option-height"
                >
                  <div class="container-fluid">
                    <div class="row" data-bs-dismiss="modal">
                      <label>
                        <div class="d-flex flex-row">
                          <div class="col-2 ps-2">
                            <input
                              type="radio"
                              class="form-check-input"
                              :value="branch.id"
                              v-model="selectedBranchId"
                            />
                          </div>

                          <div class="col-10 scss-clickable">
                            {{ branch.name }}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Term & Condition Checkbox -->
      <div class="ms-3 me-3 mt-3">
        <div class="style-font-size text-start ms-3 mb-3">
          <div class="form-check">
            <input
              type="checkbox"
              class="style-checkbox form-check-input my-auto"
              v-model="checkboxAgreements"
            />
            I agree to the
            <span
              class="scss-click-effect scss-text-blue"
              @click="
                goToLink('https://wesshelp.wessconnect.com/term-of-service/')
              "
              >Terms of Service </span
            >and<span
              class="scss-click-effect scss-text-blue"
              @click="goToLink('https://wessconnect.com/privacy-policy/')"
            >
              Privacy Policy</span
            >.
          </div>
        </div>
        <!-- Sign up button -->
        <default-button
          title="Sign up"
          type="submit"
          :disabled="buttonClickable(values, errors)"
        />
        <div class="style-font-size">
          Already have an account?
          <span class="scss-click-effect scss-text-blue" @click="goToLoginPage"
            >Login</span
          >
        </div>
      </div>
    </Form>
    <div class="scss-empty-space">&nbsp;</div>
  </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import * as yup from "yup";
import { mapGetters } from "vuex";

import DefaultButton from "@/components/button/DefaultButton.vue";
import PhoneInputField from "@/components/phoneInput/PhoneInputField.vue";
import DefaultInputField from "@/components/input/DefaultInputField.vue";
import CompanyLogo from "@/components/CompanyLogo.vue";
import { validationRules } from "@/validation/ValidationRules";
import {
  checkDuplicate,
  checkDuplicateCustomerPhone,
  checkDuplicateCustomerSharedBranch,
} from "@/services/auth";
import { useToast } from "vue-toastification";

export default {
  components: {
    Form,
    Field,
    CompanyLogo,
    DefaultButton,
    PhoneInputField,
    DefaultInputField,
  },
  name: "SignUp",
  data() {
    return {
      name: null,
      enteredPhoneNumber: null,
      password: null,
      confirmPassword: null,
      email: null,
      selectedBranchId: 1,
      checkboxAgreements: false,
      countryCode: "65",
    };
  },
  computed: {
    schema() {
      return yup.object({
        name: validationRules.name,
        phone: validationRules.phone,
        password: validationRules.password,
        confirmPassword: validationRules.confirmPassword,
        email: validationRules.email,
      });
    },
    phoneMobile() {
      return `+${this.countryCode.toString()}${this.enteredPhoneNumber}`;
    },
    selectedBranchName() {
      return this.branches.find((branch) => this.selectedBranchId === branch.id)
        ?.name;
    },

    ...mapGetters(["branches", "registerTemporary"]),
  },
  created() {
    this.reverseData();
  },
  methods: {
    reverseData() {
      if (Object.keys(this.registerTemporary).length !== 0) {
        this.name = this.registerTemporary.name;
        this.enteredPhoneNumber =
          this.registerTemporary.phoneWithoutCountryCode;
        this.password = this.registerTemporary.password;
        this.confirmPassword = this.registerTemporary.password;
        this.email = this.registerTemporary.email;
        this.selectedBranchId = this.registerTemporary.branch;
      }
    },
    goToLoginPage() {
      this.$router.push("/login");
    },
    goToLink(link) {
      const newTab = window;
      newTab.open(link, "_target");
    },
    async handleSignUp() {
      const checkSharedBranch = await checkDuplicateCustomerSharedBranch(
        this.selectedBranchId,
        this.phoneMobile
      );

      if (checkSharedBranch.data.length > 1) {
        this.$router.push("/signup/multiple-profile");
      } else {
        await checkDuplicate(
          this.phoneMobile,
          this.name,
          this.password,
          this.email,
          this.selectedBranchId
        )
          .then(async () => {
            this.$store.commit("updateRegisterTemporary", {
              name: this.name,
              phoneMobile: this.phoneMobile,
              password: this.password,
              email: this.email,
              branch: this.selectedBranchId,
              phoneWithoutCountryCode: this.enteredPhoneNumber,
            });

            await checkDuplicateCustomerPhone(
              this.phoneMobile,
              this.selectedBranchId
            ).then((res) => {
                this.$store.commit(
                  "updateDuplicateCustomerTemporary",
                  res.data
                );
                this.$router.push("/signup/account-exists");
              })
              .catch(() => {
                this.$store.commit("updateDuplicateCustomerTemporary", {});
                this.$router.push("/signup/otp");
              });
          })
          .catch(() => {
            useToast().error(
              "This mobile is already Sign up with CWA. Kindly Login to CWA instead."
            );
          });
      }
    },
    updateCountryCode(value) {
      this.countryCode = value;
    },
    buttonClickable(values, errors) {
      // Check if all field filled
      for (const value in values) {
        if (values[value] === null) {
          return true;
        }
      }

      // Check if all field is valid
      if (Object.keys(errors).length) {
        return true;
      }

      // Check if checkbox checked
      if (this.checkboxAgreements !== true) {
        return true;
      }

      return false;
    },
  },
};
</script>
<style scoped>
.style-font-size {
  font-size: 14px;
}
.style-checkbox {
  font-size: 20px;
  border-color: #ababab;
  box-shadow: none;
}
@media (min-width: 250px) {
  .modal-dialog-centered {
    min-height: calc(100% - 3.5rem);
  }
  .modal-dialog {
    max-width: 290px;
    margin: 1.75rem auto;
  }
}

.style-branch-container {
  font-size: 16px;
  border: 1px solid #adadad;
  cursor: pointer;
  height: 50px;
  background-color: white;
}

.style-branch-content {
  font-size: 16px;
  padding-top: 3px;
  padding-right: 10px;
}

.style-option-height {
  min-height: 50px;
}
</style>
